// ONBOARD-EXAMS-v2-2026-10-16: the exams the CRM can send, separate from the onboarding packet. The questions, answers
// and grading live in the portal (onboard-portal functions/_lib/exams/*.js, server-only); the database keeps the links and
// every attempt (migrations/2026-10-16-onboarding-exams.sql). This file: the catalog (the portal's ids), what each exam's
// status reads as in the Onboarding list, and the reads/writes -- dark (ready:false) until the migration is applied.

import { fetchReview } from './examReview';   // used inside a method only, so the two modules may import each other

export const EXAM_CATALOG = Object.freeze([
  { id: 'core-1', title: 'Competency Examination — Part I', short: 'Part I', disciplines: null },
  { id: 'core-2', title: 'Competency Examination — Part II', short: 'Part II', disciplines: null },
  { id: 'core-3', title: 'Competency Examination — Part III', short: 'Part III', disciplines: null },
  // only PT and PTA candidates take it (an OT or SLP version is one more entry here and one more portal data file)
  { id: 'pt-clinical', title: 'PT Clinical Competency Examination', short: 'PT clinical', disciplines: ['PT', 'PTA'] },
]);
export const EXAM_BY_ID = Object.freeze(Object.fromEntries(EXAM_CATALOG.map((e) => [e.id, e])));

// EXAMS-SEND-2026-10-16: the disciplines the CRM records (candidates.specialty, shown as the candidate's discipline). A
// discipline-specific exam is withheld only from a candidate whose discipline is one of these and not the exam's; a blank,
// "Unknown" or unrecognised discipline can be sent it, with a note. The portal applies the same rule (functions/_lib/exam.js).
export const KNOWN_DISCIPLINES = ['PT', 'PTA', 'OT', 'COTA', 'SLP', 'SLPA'];

/** -> { allowed, note, why }: may this exam be sent to a candidate of this discipline? (case-insensitive) */
export function examGate(exam, discipline) {
  const d = String(discipline || '').trim().toUpperCase();
  if (!exam || !exam.disciplines || exam.disciplines.includes(d)) return { allowed: !!exam, note: '', why: '' };
  if (KNOWN_DISCIPLINES.includes(d)) {
    return { allowed: false, note: '', why: `Written for ${exam.disciplines.join(' and ')} candidates; this candidate's discipline is ${d}.` };
  }
  return { allowed: true, note: 'This exam is written for PTs.', why: '' };
}

/** The catalog entries a candidate of this discipline can be sent. */
export function examsForDiscipline(discipline) {
  return EXAM_CATALOG.filter((e) => examGate(e, discipline).allowed);
}

/** The candidate's link to their exams: <portal>/x/<token>. */
export function examLink(token, baseUrl) {
  return `${String(baseUrl || '').replace(/\/$/, '')}/x/${token}`;
}

const t = (iso) => (iso ? new Date(iso).getTime() : NaN);
const short = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

/**
 * One exam's status for one candidate, from their links and attempts:
 *   not_sent | sent | in_progress | passed (score, date, the attempt with the certificate) | failed (attempts)
 * A passed exam stays passed whatever came after; an exam is "sent" only on a link that is open and not expired.
 */
export function examStatus(examId, links = [], attempts = [], now = Date.now()) {
  const mine = attempts.filter((a) => a.exam_id === examId).sort((a, b) => a.attempt_no - b.attempt_no);
  const done = mine.filter((a) => a.submitted_at);
  const passed = mine.find((a) => a.passed);
  if (passed) {
    return { state: 'passed', text: `Passed ${Number(passed.score_pct)}% · ${short(passed.submitted_at)}`, pct: Number(passed.score_pct),
      at: passed.submitted_at, attemptId: passed.id, certPath: passed.cert_path || null, attempts: done.length };
  }
  if (mine.some((a) => !a.submitted_at)) return { state: 'in_progress', text: 'In progress', attempts: done.length };
  if (done.length) {
    const last = done[done.length - 1];
    return { state: 'failed', text: `Failed · ${done.length} attempt${done.length === 1 ? '' : 's'} (last ${Number(last.score_pct)}%)`,
      attempts: done.length, pct: Number(last.score_pct) };
  }
  const sentOn = links.find((l) => (l.exams || []).includes(examId) && l.status === 'open' && t(l.expires_at) > now);
  if (sentOn) return { state: 'sent', text: `Sent ${short(sentOn.created_at)}${sentOn.due_on ? ` · due ${short(`${sentOn.due_on}T12:00:00`)}` : ''}` };
  return { state: 'not_sent', text: 'Not sent' };
}

/** Every catalog exam this candidate can take (by discipline), or that was sent anyway, with its status. */
export function examRow(candidate, links = [], attempts = [], now = Date.now()) {
  const offered = new Set(examsForDiscipline(candidate && candidate.specialty).map((e) => e.id));
  for (const l of links) for (const id of l.exams || []) if (EXAM_BY_ID[id]) offered.add(id);
  return EXAM_CATALOG.filter((e) => offered.has(e.id)).map((e) => ({ ...e, status: examStatus(e.id, links, attempts, now) }));
}

/** Group loaded rows by candidate: Map(candidateId -> { links, attempts }). */
export function byCandidate(links = [], attempts = []) {
  const m = new Map();
  const at = (id) => { if (!m.has(id)) m.set(id, { links: [], attempts: [] }); return m.get(id); };
  for (const l of links) at(l.candidate_id).links.push(l);
  for (const a of attempts) at(a.candidate_id).attempts.push(a);
  return m;
}

// The columns the CRM reads -- never the stored shuffle or the answers.
export const LINK_COLS = 'id,candidate_id,exams,due_on,status,created_at,created_by,expires_at,revoked_at';
export const ATTEMPT_COLS = 'id,link_id,candidate_id,exam_id,attempt_no,cert_name,started_at,submitted_at,correct,total,score_pct,passed,'
  + 'missed_areas,submit_ip,submit_user_agent,cert_path,cert_sha256,cert_issued_at';

// The tables are not there yet (migration not applied): PostgREST says so with PGRST205 / 42P01 / "does not exist".
export const isMissingTable = (e) => !!e && (/PGRST205|42P01/.test(String(e.code || '')) || /does not exist|could not find the table/i.test(String(e.message || '')));

export const EXAM_ERRORS = {
  'exam:bad_exams': 'Choose at least one exam.',
  'exam:due_in_past': 'The due date is in the past.',
  'exam:not_open': 'That exam link is already turned off.',
  'onboarding:no_such_candidate': 'That candidate no longer exists.',
};

// NIGHT-DIRECTSTAFF-2026-10-21 §3 (ruling): retakes capped at two -- the portal's rule (functions/_lib/exam.js
// attemptAllowance), mirrored so the CRM shows the lock and offers "Allow another attempt". A link made AFTER the latest
// fail is good for one more attempt; that is what Allow another attempt makes (and logs).
export const MAX_ATTEMPTS = 2;
export function retakeLock(examId, links = [], attempts = []) {
  const mine = attempts.filter((a) => a.exam_id === examId);
  const failed = mine.filter((a) => a.submitted_at && !a.passed).sort((a, b) => t(a.submitted_at) - t(b.submitted_at));
  if (mine.some((a) => a.passed) || mine.some((a) => !a.submitted_at) || failed.length < MAX_ATTEMPTS) return { locked: false, failed: failed.length };
  const last = t(failed[failed.length - 1].submitted_at);
  const extra = links.some((l) => (l.exams || []).includes(examId) && l.status === 'open' && t(l.created_at) > last
    && !mine.some((a) => t(a.started_at) >= t(l.created_at)));
  return { locked: !extra, extraOpen: extra, failed: failed.length };
}

/** Reads and writes, over the signed-in staff client. */
export function makeExamsApi(supabase) {
  // NIGHT-DIRECTSTAFF-2026-10-19 §3: a finished attempt question by question, from the portal (lib/examReview.js)
  const review = (attemptId, baseUrl) => fetchReview({ supabase, baseUrl, attemptId });
  const rpc = async (fn, args) => {
    const { data, error } = await supabase.rpc(fn, args);
    if (error) throw new Error(EXAM_ERRORS[error.message] || error.message);
    return data;
  };
  return {
    // -> { ready:false } before the migration; { ready:true, links, attempts } after (optionally for one candidate)
    async load(candidateId = null) {
      let lq = supabase.from('onboarding_exam_links').select(LINK_COLS).order('created_at', { ascending: false });
      let aq = supabase.from('onboarding_exam_attempts').select(ATTEMPT_COLS).order('attempt_no', { ascending: true });
      if (candidateId !== null) { lq = lq.eq('candidate_id', candidateId); aq = aq.eq('candidate_id', candidateId); }
      const [l, a] = await Promise.all([lq, aq]);
      if (isMissingTable(l.error) || isMissingTable(a.error)) return { ready: false };
      for (const r of [l, a]) if (r.error) throw new Error(r.error.message);
      return { ready: true, links: l.data || [], attempts: a.data || [] };
    },
    send: (candidateId, exams, dueOn = null) => rpc('onboarding_exam_send', { p_candidate_id: candidateId, p_exams: exams, p_due_on: dueOn || null }),
    revoke: (linkId) => rpc('onboarding_exam_revoke', { p_link_id: linkId }),
    review,
    // §3: one more attempt after two fails -- a new link to that exam, logged on the candidate's timeline
    async allowAnother(candidateId, examId) {
      const r = await rpc('onboarding_exam_send', { p_candidate_id: candidateId, p_exams: [examId], p_due_on: null });
      const name = EXAM_BY_ID[examId] ? EXAM_BY_ID[examId].short : examId;
      const { error } = await supabase.from('activity_log').insert({ candidate_id: candidateId, date: new Date().toISOString().split('T')[0],
        type: 'exam_allowance', note: `Allowed another attempt at ${name} (exam link ${r.link_id})` });
      return { ...r, logged: !error };
    },
    async certificateUrl(path) {
      const { data, error } = await supabase.storage.from('onboarding-docs').createSignedUrl(path, 60);
      if (error) throw new Error(error.message);
      return data.signedUrl;
    },
    // the stored certificate's SHA-256 now -- the packet's tamper check against the hash recorded at issue
    async certificateSha256(path) {
      const { data, error } = await supabase.storage.from('onboarding-docs').download(path);
      if (error || !data) return null;
      const buf = await data.arrayBuffer();
      const d = await crypto.subtle.digest('SHA-256', buf);
      return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('');
    },
  };
}

// NIGHT-DIRECTSTAFF-2026-10-17 §3: per-exam dates, and the manual reminder.
// The pass line is the portal's (each bank's passPct, graded server-side); every bank is 80 -- a test reads the portal clone.
export const PASS_PCT = 80;
export const REMIND_AFTER_DAYS = 3;
const DAY_MS = 864e5;
const dayStr = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/**
 * One exam's dates for one candidate -> { sentAt, openedAt, finishedAt, result: 'passed'|'failed'|null, pct, attempts }
 *   sent      the first link that carried it          opened    their first attempt started (the link visit itself isn't recorded)
 *   finished  the passing attempt, else the last one submitted
 */
export function examTimeline(examId, links = [], attempts = []) {
  const sent = links.filter((l) => (l.exams || []).includes(examId)).map((l) => l.created_at).filter(Boolean).sort();
  const mine = attempts.filter((a) => a.exam_id === examId).sort((a, b) => a.attempt_no - b.attempt_no);
  const done = mine.filter((a) => a.submitted_at);
  const passed = mine.find((a) => a.passed && a.submitted_at);
  const last = passed || done[done.length - 1] || null;
  return { sentAt: sent[0] || null, openedAt: mine.length ? mine[0].started_at || null : null, finishedAt: last ? last.submitted_at : null,
    result: passed ? 'passed' : done.length ? 'failed' : null, pct: last ? Number(last.score_pct) : null, attempts: done.length };
}
/** "Sent Oct 12 · Opened Oct 13 · Finished Oct 13 · Passed 86%"; '' when it was never sent. */
export function timelineText(tl) {
  if (!tl || (!tl.sentAt && !tl.openedAt)) return '';
  const parts = [];
  if (tl.sentAt) parts.push(`Sent ${short(tl.sentAt)}`);
  if (tl.openedAt) parts.push(`Opened ${short(tl.openedAt)}`);
  if (tl.finishedAt) parts.push(`Finished ${short(tl.finishedAt)}`);
  if (tl.result === 'passed') parts.push(`Passed ${tl.pct}%`);
  if (tl.result === 'failed') parts.push(`Failed ${tl.pct}%${tl.attempts > 1 ? ` (${tl.attempts} attempts)` : ''}`);
  return parts.join(' · ');
}

/**
 * The exams to remind this candidate about, or null. An exam qualifies when it is still unfinished (sent or in progress:
 * never passed or failed) and the NEWEST open link carrying it went out REMIND_AFTER_DAYS or more ago -- so a reminder
 * made yesterday quiets the button for three more days. -> { exams: [ids], dueOn, sentAt }
 */
export function reminderFor(candidate, links = [], attempts = [], now = Date.now()) {
  const live = links.filter((l) => l.status === 'open' && t(l.expires_at) > now);
  const out = [];
  let sentAt = null;
  for (const r of examRow(candidate, links, attempts, now)) {
    if (r.status.state !== 'sent' && r.status.state !== 'in_progress') continue;
    const carrying = live.filter((l) => (l.exams || []).includes(r.id)).sort((a, b) => t(b.created_at) - t(a.created_at));
    if (!carrying.length || t(carrying[0].created_at) > now - REMIND_AFTER_DAYS * DAY_MS) continue;
    out.push(r.id);
    if (!sentAt || t(carrying[0].created_at) < t(sentAt)) sentAt = carrying[0].created_at;
  }
  if (!out.length) return null;
  const today = dayStr(now);
  const dues = live.filter((l) => l.due_on && l.due_on >= today && (l.exams || []).some((id) => out.includes(id))).map((l) => l.due_on).sort();
  return { exams: out, dueOn: dues.length ? dues[dues.length - 1] : null, sentAt };
}
